import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  showErrorToast,
  showSuccessToast,
} from './SnackBar/SnackBar';

function keepEnglishSlug(value: string): string {
  return value.toLowerCase().replace(/[\/`]/g, '').replace(/[^a-z0-9-]/g, '');
}

function blockForbiddenSlugKey(event: KeyboardEvent<HTMLInputElement>) {
  if (event.key !== '/' && event.key !== '`') return;
  event.preventDefault();
}

export type PublicSlugCheck = {
  slug: string;
  available: boolean;
  reason: 'current' | 'available' | 'invalid' | 'reserved' | 'taken';
};

type PublicProfileUrlFieldProps = {
  publicPath: string;
  slug: string;
  canEdit: boolean;
  onVerify: (slug: string) => Promise<PublicSlugCheck>;
};

export function PublicProfileUrlField({
  publicPath,
  slug,
  canEdit,
  onVerify,
}: PublicProfileUrlFieldProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(() => keepEnglishSlug(slug));
  const [status, setStatus] = useState<PublicSlugCheck['reason'] | 'checking'>(
    'current',
  );
  const verifyRef = useRef(onVerify);
  verifyRef.current = onVerify;

  useEffect(() => {
    setDraft(keepEnglishSlug(slug));
    setStatus('current');
  }, [slug]);

  useEffect(() => {
    if (!canEdit) return;
    const next = keepEnglishSlug(draft);
    if (!next || next === slug) {
      setStatus('current');
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setStatus('checking');
        try {
          const check = await verifyRef.current(next);
          if (cancelled || check.slug !== next) return;
          setStatus(check.reason);
        } catch {
          if (!cancelled) setStatus('invalid');
        }
      })();
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canEdit, draft, slug]);

  const origin = window.location.origin;
  const verified =
    (status === 'current' && draft.length > 0 && draft === keepEnglishSlug(slug)) ||
    status === 'available';
  const statusKey =
    status === 'current' || status === 'available'
      ? null
      : `public-profile.slug_${status}`;

  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(`${origin}${publicPath}`);
      showSuccessToast(t('mentor-profile.link_copied'));
    } catch {
      showErrorToast(t('mentor-profile.link_copy_failed'));
    }
  }

  return (
    <section className="education-profile-page__public-url">
      <div className="education-profile-page__public-url-head">
        <h2 className="education-profile-page__public-url-title">
          {t('public-profile.url_label')}
        </h2>
        <button
          type="button"
          className="education-profile-page__public-url-copy"
          aria-label={t('public-profile.copy')}
          onClick={() => {
            void copyAddress();
          }}
        >
          <svg viewBox="0 0 24 24" aria-hidden>
            <rect
              x="8"
              y="8"
              width="12"
              height="12"
              rx="2"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            />
            <path
              d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>
      <div className="education-profile-page__public-url-control">
        <input
          className="education-profile-page__public-url-input"
          value={draft}
          disabled={!canEdit}
          lang="en"
          inputMode="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          aria-label={t('public-profile.slug')}
          onKeyDown={blockForbiddenSlugKey}
          onChange={(event) => setDraft(keepEnglishSlug(event.target.value))}
        />
        <span
          className={
            verified
              ? 'education-profile-page__public-url-mark is-verification'
              : 'education-profile-page__public-url-mark'
          }
          aria-hidden={!verified}
          aria-label={verified ? t('public-profile.slug_available') : undefined}
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
            <path
              d="M3.2 8.4 6.1 11.2 12.8 4.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>
      {statusKey ? (
        <p
          className={`education-profile-page__public-url-status education-profile-page__public-url-status--${status}`}
        >
          {t(statusKey)}
        </p>
      ) : null}
    </section>
  );
}
